import { gql } from '@/utilities/gql';
import { gqlClient } from '@/utilities/graphQLClient';
import type { QueryFunctionContext } from '@tanstack/react-query';
import type { AdditionalScreenerFilter } from '../types';

export type QueryKeyParams = {
  gridViewId: number;
  limit: number;
  initPage: number;
  displayRecentlyAddedCompanies: boolean;
  returnRecentCompaniesOnly: boolean;
  additionalFilters: AdditionalScreenerFilter[];
};

export type QueryKeyResult = [
  string,
  number,
  number,
  number,
  boolean,
  boolean,
  AdditionalScreenerFilter[],
];

export const getQueryKey = ({
  gridViewId,
  limit,
  initPage,
  displayRecentlyAddedCompanies,
  returnRecentCompaniesOnly,
  additionalFilters,
}: QueryKeyParams): QueryKeyResult => {
  return [
    'company-predefined-screeners',
    gridViewId,
    limit,
    initPage,
    displayRecentlyAddedCompanies,
    returnRecentCompaniesOnly,
    additionalFilters,
  ] as const;
};

const QUERY = gql(`
  query InvestingIdeasStocks(
    $gridViewId: Float!
    $limit: Int!
    $offset: Int!
    $displayRecentlyAddedCompanies: Boolean!
    $returnRecentCompaniesOnly: Boolean!
    $additionalFilters: [AdditionalScreenerFilter!]
  ) {
    companyPredefinedScreenerResults(
      input: {
        limit: $limit
        offset: $offset
        gridViewId: $gridViewId
        displayRecentlyAddedCompanies: $displayRecentlyAddedCompanies
        returnRecentCompaniesOnly: $returnRecentCompaniesOnly
        additionalFilters: $additionalFilters
      }
    ) {
      totalHits
      newCompaniesTotalHits
      meta {
        systemRestrictions {
          restrictionCode
          maxResultCountForRecentCompanyFeature
        }
      }
      companies {
        id
        name
        nameSlug
        canonicalURL
        exchangeSymbol
        listingCurrencyISO
        isRecentSearchEntry
        primaryIndustry {
          id
          slug
          name
        }
        score {
          dividend
          future
          health
          past
          value
        }
        tickerSymbol
        uniqueSymbol
        analysisValue {
          return1d
          return7d
          return1yAbs
          marketCap
          lastSharePrice
          priceTarget
          pe
          pb
          priceToSales
        }
        analysisMisc {
          analystCount
        }
        analysisDividend {
          dividendYield
        }
        analysisFuture {
          netIncomeGrowth3Y
          netIncomeGrowthAnnual
          revenueGrowthAnnual
        }

        info {
          url
          shortDescription
          coverSmallUrl
          yearFounded
          logoUrl
          legalName
        }
      }
    }
  }
`);

export const queryFn = async ({
  queryKey: [
    _key,
    gridViewId,
    limit,
    initPage,
    displayRecentlyAddedCompanies,
    returnRecentCompaniesOnly,
    additionalFilters,
  ],
  pageParam = initPage,
}: QueryFunctionContext<QueryKeyResult, number>) => {
  const isValid = limit > 0 && pageParam >= 0;
  if (!isValid) {
    throw new Error('Params are invalid');
  }

  const offset = pageParam * limit;

  const response = await gqlClient.request(QUERY, {
    gridViewId,
    limit,
    offset,
    displayRecentlyAddedCompanies,
    returnRecentCompaniesOnly,
    additionalFilters,
  });

  return response.companyPredefinedScreenerResults;
};
